import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { FaSun, FaMoon, FaAdjust } from 'react-icons/fa';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
  themeIcon: React.ReactNode;
  themeLabel: string;
  themeMode: 'light' | 'dark' | 'system';
  setThemeMode: (mode: 'light' | 'dark' | 'system') => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: 'light' | 'dark' | 'system';
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system'
}) => {
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(defaultTheme);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    
    const savedTheme = localStorage.getItem('themeMode');
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      const mode = savedTheme as 'light' | 'dark' | 'system';
      if (mode === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return mode === 'dark';
    }
    
    if (defaultTheme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    return defaultTheme === 'dark';
  });

  const toggleTheme = () => {
    const newMode = isDarkMode ? 'light' : 'dark';
    setThemeMode(newMode);
    setIsDarkMode(!isDarkMode);
  };

  const setTheme = (isDark: boolean) => {
    const newMode = isDark ? 'dark' : 'light';
    setThemeMode(newMode);
    setIsDarkMode(isDark);
  };

  const handleThemeModeChange = (mode: 'light' | 'dark' | 'system') => {
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
    
    if (mode === 'system') {
      setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    } else {
      setIsDarkMode(mode === 'dark');
    }
  };

  // Apply theme to document with enhanced effects
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    localStorage.setItem('themeMode', themeMode);
    
    const root = document.documentElement;
    const body = document.body;
    
    if (isDarkMode) {
      root.classList.add('dark');
      body.classList.add('dark-mode-active');
      
      // Add smooth transition class
      root.classList.add('theme-transitioning');
      setTimeout(() => {
        root.classList.remove('theme-transitioning');
      }, 300);
      
      // Add custom dark mode effects
      body.style.setProperty('--theme-transition-duration', '300ms');
    } else {
      root.classList.remove('dark');
      body.classList.remove('dark-mode-active');
      
      // Add smooth transition class
      root.classList.add('theme-transitioning');
      setTimeout(() => {
        root.classList.remove('theme-transitioning');
      }, 300);
    }
  }, [isDarkMode, themeMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Enhanced theme icon with animation
  const getThemeIcon = () => {
    if (themeMode === 'system') {
      return <FaAdjust className="w-4 h-4 animate-pulse-slow" />;
    }
    return isDarkMode ? (
      <FaSun className="w-4 h-4 animate-float" />
    ) : (
      <FaMoon className="w-4 h-4 animate-float" />
    );
  };

  const themeIcon = getThemeIcon();
  const themeLabel = isDarkMode ? 'التبديل إلى الوضع الفاتح' : 'التبديل إلى الوضع الليلي';

  const value: ThemeContextType = {
    isDarkMode,
    toggleTheme,
    setTheme,
    themeIcon,
    themeLabel,
    themeMode,
    setThemeMode: handleThemeModeChange
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Enhanced theme toggle button component
interface ThemeToggleProps {
  variant?: 'button' | 'icon' | 'minimal' | 'modern';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showLabel?: boolean;
  showMode?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'modern',
  size = 'md',
  className = '',
  showLabel = false,
  showMode = false
}) => {
  const { isDarkMode, toggleTheme, themeIcon, themeLabel, themeMode } = useTheme();

  const sizeClasses = {
    sm: 'p-2 text-sm',
    md: 'p-3 text-base',
    lg: 'p-4 text-lg'
  };

  const variantClasses = {
    button: `
      bg-white dark:bg-gray-800
      border border-gray-200 dark:border-gray-600
      text-gray-700 dark:text-gray-200
      hover:bg-gray-50 dark:hover:bg-gray-700
      shadow-sm hover:shadow-md
      rounded-lg
    `,
    icon: `
      bg-transparent
      text-gray-600 dark:text-gray-400
      hover:text-gray-800 dark:hover:text-gray-200
      hover:bg-gray-100 dark:hover:bg-gray-700
      rounded-lg
    `,
    minimal: `
      bg-transparent
      text-gray-500 dark:text-gray-400
      hover:text-gray-700 dark:hover:text-gray-200
      rounded
    `,
    modern: `
      bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700
      text-white
      hover:from-purple-600 hover:to-purple-700 dark:hover:from-purple-700 dark:hover:to-purple-800
      shadow-lg hover:shadow-xl
      rounded-xl
      backdrop-blur-sm
      border border-purple-400/20 dark:border-purple-500/20
      glow-purple dark:glow-purple
    `
  };

  const baseClasses = `
    inline-flex items-center justify-center gap-2
    font-medium transition-all duration-300
    focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
    dark:focus:ring-offset-gray-800
    transform hover:scale-105 active:scale-95
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `;

  return (
    <button
      onClick={toggleTheme}
      className={baseClasses}
      aria-label={themeLabel}
      title={themeLabel}
    >
      {themeIcon}
      {showLabel && (
        <span className="hidden sm:inline font-medium">
          {isDarkMode ? 'فاتح' : 'ليلي'}
        </span>
      )}
      {showMode && themeMode === 'system' && (
        <span className="hidden sm:inline text-xs opacity-75">
          تلقائي
        </span>
      )}
    </button>
  );
};

// Theme mode selector component
interface ThemeModeSelectorProps {
  className?: string;
}

export const ThemeModeSelector: React.FC<ThemeModeSelectorProps> = ({ className = '' }) => {
  const { themeMode, setThemeMode } = useTheme();

  const modes = [
    { value: 'light' as const, label: 'فاتح', icon: <FaSun className="w-4 h-4" /> },
    { value: 'dark' as const, label: 'ليلي', icon: <FaMoon className="w-4 h-4" /> },
    { value: 'system' as const, label: 'تلقائي', icon: <FaAdjust className="w-4 h-4" /> }
  ];

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label htmlFor="theme-mode-selector" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        نمط المظهر
      </label>
      <div id="theme-mode-selector" className="flex flex-col gap-1">
        {modes.map((mode) => (
          <button
            key={mode.value}
            onClick={() => setThemeMode(mode.value)}
            className={`
              flex items-center gap-3 px-3 py-2 rounded-lg
              transition-all duration-200
              ${themeMode === mode.value
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }
            `}
          >
            {mode.icon}
            <span className="text-sm font-medium">{mode.label}</span>
            {themeMode === mode.value && (
              <div className="ml-auto w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ThemeProvider; 